/**
 * "That is not what I played."
 *
 * ## Why this is an offer and not a validation step
 *
 * The recogniser will be wrong sometimes, and the failure mode that would kill the product
 * is the player learning to *check* it. So correction is available from a single tap on the
 * current chord, it is never demanded, and nothing is blocked while it is open — the graph
 * keeps updating behind the sheet and playing on simply closes it.
 *
 * ## Why the alternatives come first
 *
 * `DetectedChord.alternatives` are the readings the recogniser itself considered. When it
 * is wrong, the right answer is usually the runner-up, so the fastest correction is one
 * tap on a shape. Typing is the fallback, not the primary path — which is the reverse of
 * how a form would be designed, and correct here.
 *
 * Confirming dispatches `CORRECTION_APPLIED`, which keeps the node in place and re-scores
 * it. The player's position in the graph does not move because they fixed a spelling.
 */

import { useEffect, useMemo, useState } from 'react';
import type { CanonicalChord, KeyEstimate, SessionAction, SessionNode } from '@hc/shared';
import { BottomSheet } from '../system/BottomSheet.js';
import { ChordTextEntry, readChordEntry } from '../system/ChordTextEntry.js';
import { ChordSymbol } from '../system/ChordSymbol.js';
import { MiniKeyboard } from '../system/MiniKeyboard.js';
import { correctionCandidates } from '../system/chordReading.js';

export interface CorrectionSheetProps {
  open: boolean;
  node: SessionNode | null;
  keyEstimate: KeyEstimate;
  onClose: () => void;
  dispatch: (action: SessionAction) => void;
}

export function CorrectionSheet({
  open,
  node,
  keyEstimate,
  onClose,
  dispatch,
}: CorrectionSheetProps): React.JSX.Element | null {
  const [typed, setTyped] = useState('');

  // Reopening on a different chord must not inherit the last thing that was typed.
  useEffect(() => {
    if (open) setTyped('');
  }, [open, node?.id]);

  const candidates = useMemo(
    () => correctionCandidates(node?.detected, keyEstimate),
    [node?.detected, keyEstimate],
  );

  if (!node) return null;

  const apply = (chord: CanonicalChord): void => {
    dispatch({ type: 'CORRECTION_APPLIED', nodeId: node.id, chord });
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="What did you play?"
      eyebrow={
        <>
          heard as <ChordSymbol symbol={node.chord.symbol} />
        </>
      }
      testId="hc-correction-sheet"
    >
      {candidates.length > 0 ? (
        <ul className="hc-alternatives" data-testid="hc-alternatives">
          {candidates.map(({ hypothesis, chord }) => (
            <li key={`${chord.symbol}-${hypothesis.rootPc}-${hypothesis.bassPc}`}>
              <button
                type="button"
                className="hc-alternative"
                data-testid="hc-alternative"
                data-symbol={chord.symbol}
                onClick={() => apply(chord)}
              >
                <MiniKeyboard
                  pitchClasses={chord.pitchClasses}
                  bassPc={chord.bassPc}
                  label={`${chord.symbol} notes`}
                />
                <span className="hc-alternative__symbol">
                  <ChordSymbol symbol={chord.symbol} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="hc-sheet__quiet">No other reading came close. Type it instead.</p>
      )}

      <ChordTextEntry
        value={typed}
        onChange={setTyped}
        onCommit={apply}
        keyContext={keyEstimate}
        label="Or type it"
        autoFocus={false}
      />

      <div className="hc-sheet__actions">
        <button type="button" className="hc-button hc-button--quiet" onClick={onClose}>
          Leave it
        </button>
        <TypedConfirm typed={typed} keyEstimate={keyEstimate} onApply={apply} />
      </div>
    </BottomSheet>
  );
}

/**
 * The confirm button, disabled until the field holds something the parser understands.
 * Split out so the parse runs once per render of the button rather than once per keystroke
 * in the parent.
 */
function TypedConfirm({
  typed,
  keyEstimate,
  onApply,
}: {
  typed: string;
  keyEstimate: KeyEstimate;
  onApply: (chord: CanonicalChord) => void;
}): React.JSX.Element {
  const reading = useMemo(() => readChordEntry(typed, keyEstimate).chord, [typed, keyEstimate]);
  return (
    <button
      type="button"
      className="hc-button hc-button--primary"
      data-testid="hc-correction-confirm"
      disabled={!reading}
      onClick={() => {
        if (reading) onApply(reading);
      }}
    >
      {reading ? `Use ${reading.symbol}` : 'Use typed chord'}
    </button>
  );
}
